/**
 * In-memory access-token store.
 *
 * Why in-memory: stores survive only for the lifetime of the JS heap, which
 * matches the access token's short TTL (15 min) and avoids XSS exfiltration
 * of long-lived secrets. The refresh token lives in an HttpOnly Secure cookie
 * set by the backend so JS never touches it.
 *
 * Why not localStorage: localStorage is readable by any script (including
 * compromised third-party code), and tokens persist past tab close.
 *
 * On full-page reload the access token is gone — `/auth/me` (which the
 * backend will accept with just the refresh cookie via a silent refresh) is
 * used to rehydrate the session.
 */

let accessToken: string | null = null;
type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export const tokenStore = {
  get: (): string | null => accessToken,
  set: (token: string | null): void => {
    accessToken = token;
    listeners.forEach((l) => l(token));
  },
  subscribe: (l: Listener): (() => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  clear: (): void => {
    accessToken = null;
    listeners.forEach((l) => l(null));
  },
};
